import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";

export const ClippedImage = ({ src, alt, className = "", ratio = "aspect-[3/4]" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.25, 1]);
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${ratio} ${className}`}>
      <motion.img style={{ scale, y }} src={src} alt={alt} className="absolute inset-0 h-full w-full object-cover" />
    </div>
  );
};

const PRINCIPLES = [
  { n: "01", t: "Sustainable Fashion", d: "Low-impact, natural fibres for a warming world." },
  { n: "02", t: "Eco-friendly Manufacturing", d: "Hand-spun, hand-woven — zero-power at the loom." },
  { n: "03", t: "Rural Employment", d: "Dignified livelihoods across India's villages." },
  { n: "04", t: "Women Empowerment", d: "Independence woven, one thread at a time." },
  { n: "05", t: "Slow Fashion", d: "Made to last, made to matter." },
  { n: "06", t: "Handcrafted Excellence", d: "The human hand, irreplaceable and imperfectly perfect." },
  { n: "07", t: "Circular Economy", d: "Biodegradable, repairable, endlessly renewable." },
  { n: "08", t: "Made in India", d: "A symbol of freedom, self-reliance and craft." },
];

const ECOSYSTEM = [
  "Khadi Institutions", "Designers", "Fashion Institutes", "Textile Universities",
  "MSMEs", "Rural Artisans", "Youth Communities", "Retail Brands",
  "Fashion Media", "Government Agencies",
];

export const VisionMission = () => (
  <section id="vision" data-testid="vision-section" className="relative khadi-tex bg-[#f2ece0] py-28 md:py-40 px-5 md:px-10">
    <div className="mx-auto max-w-[1600px] grid md:grid-cols-12 gap-y-16 gap-x-10">
      <div className="md:col-span-4">
        <Reveal>
          <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">
            (01) The Purpose
          </p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Vision <span className="italic">&amp;</span><br />Mission
          </h2>
        </Reveal>
      </div>
      <div className="md:col-span-8 md:pl-16 border-l border-black/10 space-y-14">
        <Reveal delay={0.1}>
          <span className="font-display text-xs tracking-[0.25em] uppercase text-black/40">Vision</span>
          <p className="mt-4 font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight">
            To establish Khadi as India&apos;s most admired sustainable fashion fabric — creating
            opportunity for artisans, designers, students and entrepreneurs.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <span className="font-display text-xs tracking-[0.25em] uppercase text-black/40">Mission</span>
          <p className="mt-4 font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight text-black/70">
            To build India&apos;s largest nationwide Khadi fashion platform — connecting heritage
            with modern design through education, innovation and public engagement.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

export const WhyKhadi = () => (
  <section id="why" data-testid="why-section" className="relative khadi-tex bg-[#f2ece0] pb-28 md:pb-40 px-5 md:px-10">
    <div className="mx-auto max-w-[1600px]">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
        <div className="md:col-span-7">
          <Reveal>
            <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(02) Why Khadi Matters</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              More than a fabric — it is a symbol of India&apos;s freedom, self-reliance and craftsmanship.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-5">
          <ClippedImage
            src="https://images.pexels.com/photos/32673642/pexels-photo-32673642.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Indian artisan weaving khadi on a handloom"
            ratio="aspect-[4/3]"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-16">
        {PRINCIPLES.map((p, i) => (
          <Reveal key={p.n} delay={(i % 2) * 0.08}>
            <div className="group relative border-t border-black/10 py-8 flex items-start gap-6 transition-colors duration-500 hover:border-[#a65a34]">
              <span className="font-serif italic text-4xl md:text-5xl text-black/20 group-hover:text-[#a65a34] transition-colors duration-500 w-16 shrink-0">
                {p.n}
              </span>
              <div>
                <h3 className="font-display text-lg md:text-xl uppercase tracking-tight">{p.t}</h3>
                <p className="mt-2 font-body text-sm text-black/50 leading-relaxed">{p.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const NationalImpact = () => (
  <section id="impact" data-testid="impact-section" className="relative khadi-tex-dark bg-[#1a130b] text-[#f2ece0] py-28 md:py-40 px-5 md:px-10 grain">
    <div className="mx-auto max-w-[1600px]">
      <div className="grid md:grid-cols-12 gap-8 mb-16">
        <div className="md:col-span-8">
          <Reveal>
            <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(03) National Impact</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              One nationwide ecosystem, <span className="italic text-[#a65a34]">woven together.</span>
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-4 flex md:items-end">
          <Reveal delay={0.15}>
            <p className="font-body text-sm text-[#f2ece0]/60 leading-relaxed">
              The initiative connects every thread of India&apos;s textile future — from the loom in
              a village to the runway in a metro, and every studio, campus and boardroom between.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-6">
        {ECOSYSTEM.map((e, i) => {
          // asymmetric spans for bento rhythm
          const spans = ["md:col-span-5", "md:col-span-4", "md:col-span-3", "md:col-span-3", "md:col-span-4", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-6"];
          return (
            <Reveal key={e} delay={i * 0.05} className={`col-span-1 ${spans[i]}`}>
              <div className="group h-full border border-[#f2ece0]/12 p-6 md:p-8 flex items-end justify-between min-h-[120px] md:min-h-[160px] transition-colors duration-500 hover:bg-[#f2ece0] hover:text-[#1a130b]">
                <h3 className="font-serif text-xl md:text-3xl leading-tight tracking-tight">{e}</h3>
                <span className="font-display text-[10px] tracking-widest opacity-40">{String(i + 1).padStart(2, "0")}</span>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default VisionMission;
